import { useState } from 'react';
import { KeyIcon } from '@heroicons/react/24/outline';
import { Button, Card, Input } from '@/components/common';
import { useChangePassword } from '@/hooks/useAuth';

/**
 * "Reset Password", lifted out of `MyContactsPage` (where it was a file-local,
 * unexported component) so the ONE card serves both portal Settings pages —
 * the customer's and the POSP's. C1 and P1 both asked for it, and they are the
 * same screen underneath: `/my-contacts` renders the same component for both
 * roles, only the sidebar label differs.
 *
 * Posts to `/users/change_password/` — the same endpoint the generic Settings
 * page's Password tab uses, so the two can never disagree about what a
 * password change means.
 */
export function ResetPasswordCard() {
  const changePassword = useChangePassword();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (newPassword !== confirmPassword) {
      setLocalError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === oldPassword) {
      setLocalError('New password must be different from your current password');
      return;
    }
    if (newPassword.length < 8) {
      setLocalError('New password must be at least 8 characters.');
      return;
    }
    // CUSTOMER-7: the serializer requires `new_password_confirm`. The form
    // collected it but never sent it, so every submit failed with
    // "New Password Confirm is required".
    changePassword.mutate(
      { old_password: oldPassword, new_password: newPassword, new_password_confirm: confirmPassword },
      {
        onSuccess: () => {
          setOldPassword('');
          setNewPassword('');
          setConfirmPassword('');
        },
      }
    );
  };

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
            <KeyIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Reset Password</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            autoComplete="current-password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
          <Input
            label="New Password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <Input
            label="Confirm New Password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={localError ?? undefined}
            required
          />
          <Button type="submit" isLoading={changePassword.isPending} disabled={!oldPassword || !newPassword}>
            Update Password
          </Button>
        </form>
      </div>
    </Card>
  );
}
